import { bulkPdfError, bulkPdfErrorCode } from './bulkPdfErrors.js'

// One bounded preparation contract for Chromium's native PDF, never a capture
// fallback. The caller must emulate print media BEFORE invoking this function.
export async function prepareServerReportPdf({ ready, error, prepare, fit, signal,
  permitted, doc = document, frame = callback => requestAnimationFrame(callback), timeoutMs = 30000 }) {
  let timer, poll, abort
  const check = () => {
    if (signal?.aborted) throw bulkPdfError('cancelled')
    if (!permitted()) throw bulkPdfError('auth')
    const failure = error()
    if (failure) throw bulkPdfError(bulkPdfErrorCode(failure, 'report'))
  }
  const boundary = new Promise((_, reject) => {
    abort = () => reject(bulkPdfError('cancelled'))
    signal?.addEventListener('abort', abort, { once: true })
    timer = setTimeout(() => reject(bulkPdfError('timeout')), timeoutMs)
    // Observe sticky dependency errors and permission revocation even while
    // fonts/chart work or a real animation frame is still pending.
    poll = setInterval(() => { try { check() } catch (e) { reject(e) } }, 25)
  })
  const wait = async promise => { check(); const result = await Promise.race([promise, boundary]); check(); return result }
  try {
    check()
    while (!ready()) await wait(new Promise(resolve => setTimeout(resolve, 25)))
    await wait(Promise.resolve().then(prepare))
    if (doc.fonts) await wait(doc.fonts.ready)
    await wait(new Promise(resolve => frame(resolve)))
    await wait(new Promise(resolve => frame(resolve)))
    // Resizing/fonts can schedule another chart update; do not print stale data.
    while (!ready()) await wait(new Promise(resolve => setTimeout(resolve, 25)))
    const pages = [...doc.querySelectorAll('#report-content .report-page')]
    if (pages.length !== 4 || pages.some(page => !page.getBoundingClientRect().width || !page.getBoundingClientRect().height)) throw bulkPdfError('render')
    fit()
    const sheets = [...doc.querySelectorAll('#report-content > .native-print-sheet')]
    if (sheets.length !== 4 || sheets.some((sheet, index) => sheet.children.length !== 1 || sheet.firstElementChild !== pages[index] || !sheet.getBoundingClientRect().height)) throw bulkPdfError('render')
    check()
    return { pages: 4 }
  } finally {
    clearTimeout(timer); clearInterval(poll)
    signal?.removeEventListener('abort', abort)
  }
}
